import React from 'react';
import { Navigate } from 'react-router-dom';
import { ROUTE_DEFINITIONS } from '@/routes';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import type { SectionName } from '@/types/sections';


interface ProtectedByPermissionProps {
  /** Route code to check. If omitted, auto-resolves from current URL. */
  screenCode?: string;
  /**
   * Section that must be readable, checked on top of the screen-level permission. For routes that
   * are a section of their parent screen: the screen code alone would admit a role that holds the
   * screen without the section, which then gets a 403 on every request the page makes.
   */
  section?: SectionName;
  children: React.ReactNode;
}

/**
 * Route-level guard that redirects to home if the user cannot view the screen.
 *
 * @example
 * // In App.tsx or wrapping a page component
 * <ProtectedByPermission>
 *   <EmployeeCreate />
 * </ProtectedByPermission>
 */
export const ProtectedByPermission: React.FC<ProtectedByPermissionProps> = ({
  screenCode,
  section,
  children,
}) => {
  const { canView, canSection, isLoaded } = usePagePermissions(screenCode);

  // Wait for permissions to load before making a decision
  if (!isLoaded) return null;

  const allowed = canView && (!section || canSection(section, 'view'));
  if (!allowed) {
    return <Navigate to={ROUTE_DEFINITIONS.ACCESO_DENEGADO.path} replace />;
  }

  return <>{children}</>;
};
